import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Spinner } from 'reactstrap';
import { BiLockAlt, BiCheckCircle, BiErrorCircle } from 'react-icons/bi';
import { toast } from 'react-toastify';
import useTierFeatures from '../../hooks/useTierFeatures';
import useTheme from '../../hooks/useTheme';
import xeroLogo from '../../assets/integrations/xero.svg';
import quickbooksLogo from '../../assets/integrations/quickbooks.svg';
import sageLogo from '../../assets/integrations/sage.svg';
import sageLogoWhite from '../../assets/integrations/sage-white.svg';
// Official Xero buttons (Xero App Partner brand guidelines — don't restyle).
// Blue for light backgrounds, white for dark.
import xeroConnectBlue from '../../assets/integrations/blue-connect.png';
import xeroConnectWhite from '../../assets/integrations/white-connect.png';
import xeroDisconnectBlue from '../../assets/integrations/blue-disconnect.png';
import xeroDisconnectWhite from '../../assets/integrations/white-disconnect.png';
// Official Intuit "Connect to QuickBooks" button (green, brand guidelines —
// don't restyle). The green reads on light and dark, so one asset covers both.
import quickbooksConnect from '../../assets/integrations/quickbooks-connect.svg';
import {
    fetchConnections,
    fetchSettings,
    startConnect,
    disconnect,
    updateSettings,
    fetchTenants,
    selectTenant,
    syncAllCustomers,
} from '../../api/integrationsApi';

// Xero/QuickBooks are self-contained colour icons that read on any background.
// Sage is a wordmark, so it gets a white variant for dark mode. logoHeight
// keeps the mixed icon/wordmark shapes visually balanced.
const PROVIDERS = [
    { slug: 'xero', name: 'Xero', live: true, logoHeight: 40, blurb: 'Sync invoices, bills, contacts and payments.' },
    { slug: 'quickbooks', name: 'QuickBooks', live: true, logoHeight: 40, blurb: 'Invoices, bills and contacts.' },
    { slug: 'sage', name: 'Sage', live: false, logoHeight: 26, blurb: 'Invoices, bills and contacts.' },
];

const STATUS_COPY = {
    connected: { label: 'Connected', color: '#10b981' },
    reconnectRequired: { label: 'Reconnect required', color: '#ef4444' },
    disconnected: { label: 'Disconnected', color: '#64748b' },
};

// Mapping fields autosave on a debounce, matching the rest of the app's
// settings inputs (e.g. company branding on settingsPage).
const DEBOUNCE_MS = 750;

export default function IntegrationsSection() {
    const { accountingEnabled, isLoading: tierLoading } = useTierFeatures();
    const isDark = useTheme() === 'dark-mode';
    const [connections, setConnections] = useState([]);
    const [settings, setSettings] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Theme-aware brand assets.
    const logoFor = (slug) => (slug === 'xero' ? xeroLogo : slug === 'quickbooks' ? quickbooksLogo : (isDark ? sageLogoWhite : sageLogo));
    const xeroConnectImg = isDark ? xeroConnectWhite : xeroConnectBlue;
    const xeroDisconnectImg = isDark ? xeroDisconnectWhite : xeroDisconnectBlue;

    const load = useCallback(async () => {
        try {
            const [conns, setts] = await Promise.all([fetchConnections(), fetchSettings()]);
            setConnections(conns ?? []);
            setSettings(setts);
        } catch { /* connect-action toasts surface errors */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { if (accountingEnabled) load(); else setLoading(false); }, [accountingEnabled, load]);

    useEffect(() => {
        const result = searchParams.get('accounting');
        if (!result) return;
        if (result === 'connected') toast.success('Accounting connected.');
        else if (result === 'failed') toast.error('Connection failed. Please try again.');
        else if (result === 'expired') toast.error('Connection timed out. Please try again.');
        searchParams.delete('accounting');
        setSearchParams(searchParams, { replace: true });
        load();
    }, [searchParams, setSearchParams, load]);

    const active = connections.find((c) => c.status !== 'disconnected');

    useEffect(() => {
        if (!active) { setTenants([]); return; }
        fetchTenants(active.provider).then(setTenants).catch(() => setTenants([]));
    }, [active]);

    if (tierLoading || loading) {
        return (
            <div className="surface p-4 mb-4">
                <h2 className="fw-semibold fs-5 mb-3">Accounting integrations</h2>
                <Spinner size="sm" />
            </div>
        );
    }

    if (!accountingEnabled) {
        return (
            <div className="surface p-4 mb-4">
                <h2 className="fw-semibold fs-5 mb-2">Accounting integrations</h2>
                <p className="text-muted mb-0 d-flex align-items-center gap-2">
                    <BiLockAlt /> Sync invoices and expenses to Xero, QuickBooks or Sage on the
                    Pro and Business plans.
                </p>
            </div>
        );
    }

    const handleConnect = async (slug) => {
        setBusy(true);
        try {
            const returnTo = `${window.location.origin}/settings`;
            const { authUrl } = await startConnect(slug, returnTo);
            window.location.href = authUrl;
        } catch (err) {
            toast.error(err?.message ?? 'Could not start connection.');
            setBusy(false);
        }
    };

    const handleDisconnect = async () => {
        if (!active) return;
        setBusy(true);
        try {
            await disconnect(active.provider);
            toast.success('Disconnected.');
            await load();
        } catch (err) {
            toast.error(err?.message ?? 'Could not disconnect.');
        } finally { setBusy(false); }
    };

    const saveSettings = async (patch) => {
        setBusy(true);
        try {
            const next = await updateSettings({
                autoSync: settings?.autoSync ?? true,
                defaultSalesAccountCode: settings?.defaultSalesAccountCode ?? null,
                defaultPurchaseAccountCode: settings?.defaultPurchaseAccountCode ?? null,
                defaultTaxType: settings?.defaultTaxType ?? null,
                defaultSalesItem: settings?.defaultSalesItem ?? null,
                contactMatchStrategy: settings?.contactMatchStrategy ?? 'email',
                ...patch,
            });
            setSettings(next);
            toast.success('Saved.');
        } catch (err) {
            toast.error(err?.message ?? 'Could not save.');
        } finally { setBusy(false); }
    };

    const handleSyncAllCustomers = async () => {
        setBusy(true);
        try {
            const { pulled, linked, pushed } = await syncAllCustomers();
            toast.success(`Customers synced — imported ${pulled}, linked ${linked}, ${pushed} queued to push.`);
            await load();
        } catch (err) {
            toast.error(err?.message ?? 'Could not sync customers.');
        } finally { setBusy(false); }
    };

    const handleSelectTenant = async (tenantId) => {
        if (!active) return;
        setBusy(true);
        try {
            await selectTenant(active.provider, tenantId);
            await load();
        } catch (err) {
            toast.error(err?.message ?? 'Could not change company.');
        } finally { setBusy(false); }
    };

    // Official brand connect/disconnect buttons render as fixed artwork (height
    // pinned, width auto) — used for Xero and QuickBooks, which both mandate
    // their own button image. Providers without one fall back to a text button.
    const brandConnectImg = (slug) => (slug === 'xero' ? xeroConnectImg : slug === 'quickbooks' ? quickbooksConnect : null);
    const BrandImgButton = ({ src, label, onClick }) => (
        <button
            type="button"
            disabled={busy}
            onClick={onClick}
            className="p-0 border-0 bg-transparent"
            style={{ cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}
            aria-label={label}
        >
            <img src={src} alt={label} height={38} />
        </button>
    );

    return (
        <div className="surface p-4 mb-4">
            <h2 className="fw-semibold fs-5 mb-1">Accounting integrations</h2>
            <p className="text-muted mb-4">
                Push invoices and expenses to your accounting package and pull payment status back.
            </p>

            {!active && (
                <div className="row g-3">
                    {PROVIDERS.map((p) => (
                        <div className="col-12 col-md-4" key={p.slug}>
                            <div
                                className="h-100 border rounded-3 p-4 d-flex flex-column position-relative"
                                style={{ background: p.live ? 'transparent' : 'rgba(127,127,127,0.05)' }}
                            >
                                {!p.live && (
                                    <span
                                        className="position-absolute badge rounded-pill fw-semibold"
                                        style={{ top: 14, right: 14, background: 'rgba(127,127,127,0.16)', color: '#64748b', fontSize: 11 }}
                                    >
                                        Coming soon
                                    </span>
                                )}
                                <div
                                    className="d-flex align-items-center mb-3"
                                    style={{ height: 44, filter: p.live ? 'none' : 'grayscale(1)', opacity: p.live ? 1 : 0.5 }}
                                >
                                    <img src={logoFor(p.slug)} alt={p.name} style={{ height: p.logoHeight, width: 'auto' }} />
                                </div>
                                <div className="fw-semibold fs-6 mb-1" style={{ opacity: p.live ? 1 : 0.6 }}>{p.name}</div>
                                <div className="text-muted small mb-3 flex-grow-1">{p.blurb}</div>
                                {p.live && brandConnectImg(p.slug) && (
                                    <div className="align-self-start">
                                        <BrandImgButton src={brandConnectImg(p.slug)} label={`Connect to ${p.name}`} onClick={() => handleConnect(p.slug)} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {active && (
                <div>
                    <div className="border rounded-3 p-3 d-flex align-items-center gap-3 mb-3">
                        <img src={logoFor(active.provider)} alt={active.provider} style={{ height: active.provider === 'sage' ? 24 : 40, width: 'auto' }} />
                        <div className="flex-grow-1">
                            <div className="fw-semibold text-capitalize">
                                {active.provider}
                                {active.externalTenantName ? <span className="text-muted fw-normal"> · {active.externalTenantName}</span> : null}
                            </div>
                            <div className="d-flex align-items-center gap-1 small" style={{ color: (STATUS_COPY[active.status] || STATUS_COPY.disconnected).color }}>
                                {active.status === 'connected' ? <BiCheckCircle size={15} /> : <BiErrorCircle size={15} />}
                                {(STATUS_COPY[active.status] || STATUS_COPY.disconnected).label}
                            </div>
                        </div>
                        {active.status === 'reconnectRequired'
                            ? (brandConnectImg(active.provider)
                                ? <BrandImgButton src={brandConnectImg(active.provider)} label={`Reconnect to ${active.provider}`} onClick={() => handleConnect(active.provider)} />
                                : <Button size="sm" color="primary" disabled={busy} onClick={() => handleConnect(active.provider)}>Reconnect</Button>)
                            : (active.provider === 'xero'
                                ? <BrandImgButton src={xeroDisconnectImg} label="Disconnect from Xero" onClick={handleDisconnect} />
                                : (
                                    <Button size="sm" color="link" className="text-danger text-decoration-none" disabled={busy} onClick={handleDisconnect}>
                                        Disconnect
                                    </Button>
                                ))}
                    </div>

                    {tenants.length > 1 && (
                        <label className="d-flex flex-column gap-1 mb-3" style={{ maxWidth: 360 }}>
                            <span className="small text-muted">Linked company</span>
                            <Input
                                type="select"
                                value={tenants.find((t) => t.name === active.externalTenantName)?.id ?? ''}
                                disabled={busy}
                                onChange={(e) => handleSelectTenant(e.target.value)}
                            >
                                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </Input>
                        </label>
                    )}

                    <div className="border rounded-3 p-3 d-flex align-items-start justify-content-between gap-3 mb-3">
                        <div>
                            <div className="fw-semibold">Auto Sync</div>
                            <div className="text-muted small">
                                Automatically push finalised invoices and new expenses. When off, records only sync when you press “Send”.
                            </div>
                        </div>
                        <div className="form-check form-switch m-0" style={{ minWidth: 40 }}>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                style={{ cursor: 'pointer' }}
                                checked={settings?.autoSync ?? true}
                                disabled={busy}
                                onChange={(e) => saveSettings({ autoSync: e.target.checked })}
                            />
                        </div>
                    </div>

                    <div className="mb-1 fw-semibold">Default mapping</div>
                    <div className="text-muted small mb-2">
                        Applied to every pushed record. Use your accounting package’s account and tax codes.
                    </div>
                    <div className="d-flex flex-wrap gap-3">
                        {active.provider === 'quickbooks'
                            ? (
                                // QuickBooks sales lines reference a product/service Item, not a
                                // ledger account code — so QBO maps a Sales item + Expense account.
                                <MappingInput label="Sales item" placeholder="e.g. Services" value={settings?.defaultSalesItem}
                                    onSave={(v) => saveSettings({ defaultSalesItem: v })} disabled={busy} />
                            ) : (
                                <MappingInput label="Sales account code" placeholder="e.g. 200" value={settings?.defaultSalesAccountCode}
                                    onSave={(v) => saveSettings({ defaultSalesAccountCode: v })} disabled={busy} />
                            )}
                        <MappingInput label={active.provider === 'quickbooks' ? 'Expense account' : 'Purchase account code'} placeholder="e.g. 400" value={settings?.defaultPurchaseAccountCode}
                            onSave={(v) => saveSettings({ defaultPurchaseAccountCode: v })} disabled={busy} />
                        <MappingInput label="Tax / VAT code" placeholder={active.provider === 'quickbooks' ? 'e.g. 4' : 'e.g. OUTPUT2'} value={settings?.defaultTaxType}
                            onSave={(v) => saveSettings({ defaultTaxType: v })} disabled={busy} />
                    </div>

                    <hr className="my-3" />
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                            <div className="fw-semibold">Customers</div>
                            <div className="text-muted small">
                                Two-way sync: imports customers from {active.provider} into MarginPal and pushes
                                yours up, linking matches by email. Great for first-time setup; new customers
                                also sync automatically when you send their invoices.
                            </div>
                        </div>
                        <Button color="primary" outline size="sm" disabled={busy} onClick={handleSyncAllCustomers}>
                            Sync customers
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function MappingInput({ label, value, placeholder, onSave, disabled }) {
    const [local, setLocal] = useState(value ?? '');
    const timerRef = useRef(null);
    // Reflect external changes (e.g. settings re-fetched after a save).
    useEffect(() => { setLocal(value ?? ''); }, [value]);
    // Cancel a pending debounced save if the field unmounts.
    useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleChange = (e) => {
        const next = e.target.value;
        setLocal(next);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if ((next || '') !== (value || '')) onSave(next || null);
        }, DEBOUNCE_MS);
    };

    return (
        <label className="d-flex flex-column gap-1" style={{ maxWidth: 220, flex: '1 1 180px' }}>
            <span className="small text-muted">{label}</span>
            <Input
                type="text"
                value={local}
                placeholder={placeholder}
                disabled={disabled}
                onChange={handleChange}
            />
        </label>
    );
}
